import firebaseConfig from 'firebaseConfig';
import {Article} from '@/model/Article';
import {config} from '@/config';
import {Environment} from '@/utils/Environment';
import {Functions} from '@/plugins/Functions';
import {Firestore} from '@/plugins/Firestore';
import {Store} from '@/plugins/Store';

export const SHARE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;

/**
 * Same list as SHARE_PREVIEW_FIELDS in functions/src/Article/shareSanitize.ts:
 * only what ArticleView renders in the share preview (#30940).
 */
export const SHARE_PREVIEW_FIELDS: readonly string[] = [
    'id',
    'shareToken',
    'isAnalyze',
    // keyword analysis (read-only)
    'keyword',
    'selectedKeyword',
    'secondaryKeywords',
    'allKeywords',
    'keywordsForAi',
    'aiWritingBrief',
    'geoTemplate',
    'tone',
    'audience',
    'depth',
    'language_code',
    'location_code',
    // article body, SERP preview and URL suggestion
    'url',
    'metaTitle',
    'metaDescription',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'questions',
    'images',
    'schemaJsonLd',
    'eatAttribution',
    // SEO menu and the generation status line
    'seoScore',
    'created_at',
    'enhanced',
    'aiGenerateStatus',
];

/**
 * The aiWritingBrief subfields the share preview renders: the AI Lucy panel
 * (AiWritingBriefPanel) and the read-only keyword analysis (KeywordSelection).
 * The writing instructions picked in the brief editor (selectedFacts,
 * introDirectives) feed the AI prompt and stay private, like every other
 * subfield the preview does not show (#30940).
 */
export const SHARE_PREVIEW_BRIEF_FIELDS: readonly string[] = [
    // AI Lucy panel
    'intent',
    'snippetShape',
    'keepAnswerShape',
    'suggestedGeoTemplate',
    'answers',
    'citedSources',
    'selectedCitationUrls',
    'workedInPlatforms',
    // keyword analysis
    'peopleAlsoSearch',
    'autocomplete',
    'gapTopics',
];

export function isSharePreviewRoute(routeName: string | null | undefined): boolean {
    return routeName === 'SharedArticle';
}

export function newShareToken(): string {
    const bytes = new Uint8Array(18);
    crypto.getRandomValues(bytes);
    let binary = '';
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function sharePath(token: string): string {
    return `/s/${token}`;
}

export function shareUrl(token: string): string {
    if (typeof window === 'undefined') {
        return sharePath(token);
    }
    return `${window.location.origin}${sharePath(token)}`;
}

function sanitizeBriefForShare(brief: unknown): Record<string, unknown> | null {
    if (!brief || typeof brief !== 'object' || Array.isArray(brief)) {
        return null;
    }
    const source = brief as Record<string, unknown>;
    const kept: Record<string, unknown> = {};
    SHARE_PREVIEW_BRIEF_FIELDS.forEach((key) => {
        if (source[key] !== undefined) {
            kept[key] = source[key];
        }
    });
    return kept;
}

export function sanitizeArticleForShare(raw: Record<string, unknown> | Article | null | undefined): Record<string, unknown> {
    // toObject leaves out shareToken (the server writes it); the preview keeps it.
    const source = (raw && typeof raw === 'object'
        ? (typeof (raw as Article).toObject === 'function'
            ? {...(raw as Article).toObject(), shareToken: (raw as Article).shareToken}
            : raw)
        : {}) as Record<string, unknown>;
    const article: Record<string, unknown> = {};
    SHARE_PREVIEW_FIELDS.forEach((key) => {
        const value = source[key];
        if (value === undefined) {
            return;
        }
        article[key] = value;
    });
    if (typeof (raw as Article | undefined)?.id === 'string') {
        article.id = (raw as Article).id;
    }
    if (article.aiWritingBrief !== undefined) {
        article.aiWritingBrief = sanitizeBriefForShare(article.aiWritingBrief);
    }
    article.isAnalyze = !!article.isAnalyze;
    return article;
}

function functionsOrigin(): string {
    return `https://${config.region}-${firebaseConfig.projectId}.cloudfunctions.net`;
}

async function postLocalSnapshot(token: string, article: Record<string, unknown>): Promise<void> {
    if (!Environment.isLocalPreviewHost()) {
        return;
    }
    await fetch('/__share-preview', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({token, article}),
    });
}

async function deleteLocalSnapshot(token: string): Promise<void> {
    if (!Environment.isLocalPreviewHost()) {
        return;
    }
    await fetch(`/__share-preview?token=${encodeURIComponent(token)}`, {method: 'DELETE'});
}

async function fetchLocalSnapshot(token: string): Promise<Record<string, unknown> | null> {
    if (!Environment.isLocalPreviewHost()) {
        return null;
    }
    const response = await fetch(`/__share-preview?token=${encodeURIComponent(token)}`);
    if (!response.ok) {
        return null;
    }
    const payload = await response.json();
    if (payload && payload.article && typeof payload.article === 'object') {
        return payload.article as Record<string, unknown>;
    }
    return null;
}

async function fetchCloudSnapshot(token: string): Promise<Record<string, unknown> | null> {
    const response = await fetch(
        `${functionsOrigin()}/GetSharedArticle?token=${encodeURIComponent(token)}`
    );
    if (!response.ok) {
        return null;
    }
    const payload = await response.json();
    if (payload && payload.article && typeof payload.article === 'object') {
        return payload.article as Record<string, unknown>;
    }
    return null;
}

async function fetchOwnerSnapshot(token: string): Promise<Record<string, unknown> | null> {
    if (!Store.getters.authenticated) {
        return null;
    }
    const articleId = Store.getters.article?.id;
    const current = Store.getters.article as Article | undefined;
    if (current && current.shareToken === token && current.id) {
        return sanitizeArticleForShare(current);
    }
    if (articleId) {
        const article = await Firestore.getUserArticle(articleId);
        if (article && article.shareToken === token) {
            return sanitizeArticleForShare(article);
        }
    }
    return null;
}

/**
 * Asks CreateShareLink every time, also when the article already has a token:
 * only the server knows whether shareLinks/{token} exists. On a hosted app a
 * failure is an error for the caller; a token that only sits on the article
 * would give the recipient a 404 (#30940).
 */
export async function ensureShareLink(article: Article): Promise<string> {
    if (!article.id) {
        throw new Error('missing_article');
    }

    try {
        const data = await Functions.CreateShareLink({articleId: article.id});
        if (!data || !data.token || !SHARE_TOKEN_PATTERN.test(data.token)) {
            throw new Error('invalid_share_token');
        }
        article.shareToken = data.token;
        const snapshot = sanitizeArticleForShare(article);
        snapshot.shareToken = data.token;
        await postLocalSnapshot(data.token, snapshot).catch(() => undefined);
        return data.token;
    } catch (error) {
        if (!Environment.isLocalPreviewHost()) {
            throw error;
        }
        // Local Preview without deployed functions: the token lives on the
        // article and the snapshot in /__share-preview.
    }

    if (article.shareToken && SHARE_TOKEN_PATTERN.test(article.shareToken)) {
        const snapshot = sanitizeArticleForShare(article);
        snapshot.shareToken = article.shareToken;
        await postLocalSnapshot(article.shareToken, snapshot).catch(() => undefined);
        return article.shareToken;
    }

    const token = newShareToken();
    article.shareToken = token;
    await Firestore.updateArticleShareToken(article, token);
    const snapshot = sanitizeArticleForShare(article);
    snapshot.shareToken = token;
    await postLocalSnapshot(token, snapshot).catch(() => undefined);
    return token;
}

/**
 * Revokes the article's share links (D7: manual only, no expiry). Old links
 * stop working; the next Share creates a new one.
 */
export async function revokeShareLink(article: Article): Promise<void> {
    if (!article.id) {
        throw new Error('missing_article');
    }
    const token = article.shareToken;
    try {
        await Functions.RevokeShareLink({articleId: article.id});
    } catch (error) {
        if (!Environment.isLocalPreviewHost()) {
            throw error;
        }
        await Firestore.updateArticleShareToken(article, null);
    }
    if (token) {
        await deleteLocalSnapshot(token).catch(() => undefined);
    }
    article.shareToken = null;
}

export async function fetchSharedArticle(token: string): Promise<Article> {
    if (!SHARE_TOKEN_PATTERN.test(token)) {
        throw new Error('invalid_token');
    }

    const local = await fetchLocalSnapshot(token).catch(() => null);
    const cloud = local || await fetchCloudSnapshot(token).catch(() => null);
    const raw = cloud || await fetchOwnerSnapshot(token).catch(() => null);
    if (!raw) {
        throw new Error('not_found');
    }
    raw.shareToken = token;
    return Article.fromObject(raw);
}

export function applySharedArticleToStore(article: Article): void {
    Store.commit('setSharePreview', {active: true, token: article.shareToken || null});
    Store.dispatch('setArticle', article);
    Store.commit('setArticleSeoScoreChecked', true);
    Store.commit('setArticleSeoScoreAiLoadingCompleted', true);
    Store.commit('setArticleAnalyzeScrapeLoading', false);
    Store.commit('setArticleLastSavedSnapshotHash', 'share-preview');
}
